import styled from 'styled-components';
import * as MdIcons from 'react-icons/md';
import { useModalProvider } from "../../Providers/ModalProvider";
import ModalHeader from "../../Components/ModalHeader/ModalHeader";
import ChangeEmail from "../../Pages/Account/AccountSettingsModalSubpages/ChangeEmail"
import ChangePassword from '../../Pages/Account/AccountSettingsModalSubpages/ChangePassword';
import DeleteAccount from '../../Pages/Account/AccountSettingsModalSubpages/DeleteAccount';
import { useAuthProvider } from '../../Providers/AuthProvider';
import { SOFTWARE_VERSION } from '../../Version';
import ModalScrollbar from '../ModalScrollbar/ModalScrollbar';
import { MobergButton, MobergButtonVariant, MobergButtonShape, MobergTheme } from '../../Moberg';

const AccountSettingsModal = (props) => {
    const { openNext } = useModalProvider()
    const authProvider = useAuthProvider()

    function renderChangeEmailModal() {
        openNext(<ChangeEmail
            escClose={false}
            clickOutsideClose={false}
        />)
    }

    function renderChangePasswordModal() {
        openNext(<ChangePassword
            escClose={false}
            clickOutsideClose={false}
        />)
    }

    function renderDeleteAccountModal() {
        openNext(<DeleteAccount
            escClose={false}
            clickOutsideClose={false}
        />)
    }

    return (
        <ModalContainer>
            <ModalHeader headerText='Account Settings' />
            <ModalScrollbar>
                <ContentContainer>
                    <SectionCard>
                        <CardHeader>
                            <SectionTitle>Account</SectionTitle>
                        </CardHeader>
                        <CardDescription>
                            Your personal information and account access settings
                        </CardDescription>
                        <CardContent>
                            <SettingRow>
                                <SettingInfo>
                                    <IconWrapper>
                                        <MdIcons.MdEmail size={20} style={{ color: 'var(--text-secondary-color)' }} />
                                    </IconWrapper>
                                    <SettingDetails>
                                        <SettingLabel>Email Address</SettingLabel>
                                        <SettingValue>{authProvider.currentUser?.email}</SettingValue>
                                    </SettingDetails>
                                </SettingInfo>
                                <MobergButton
                                    theme={MobergTheme.BLUE}
                                    variant={MobergButtonVariant.CLEAR}
                                    shape={MobergButtonShape.RECTANGLE}
                                    onClick={renderChangeEmailModal}
                                >
                                    Edit
                                </MobergButton>
                            </SettingRow>

                            <SettingDivider />

                            <SettingRow>
                                <SettingInfo>
                                    <IconWrapper>
                                        <MdIcons.MdLock size={20} style={{ color: 'var(--text-secondary-color)' }} />
                                    </IconWrapper>
                                    <SettingDetails>
                                        <SettingLabel>Password</SettingLabel>
                                        <SettingValue>••••••••</SettingValue>
                                    </SettingDetails>
                                </SettingInfo>
                                <MobergButton
                                    theme={MobergTheme.BLUE}
                                    variant={MobergButtonVariant.CLEAR}
                                    shape={MobergButtonShape.RECTANGLE}
                                    onClick={renderChangePasswordModal}
                                >
                                    Change
                                </MobergButton>
                            </SettingRow>

                            <SettingDivider />

                            <SettingRow>
                                <SettingInfo>
                                    <IconWrapper>
                                        <MdIcons.MdDeleteOutline size={20} style={{ color: '#e64e58' }} />
                                    </IconWrapper>
                                    <SettingDetails>
                                        <SettingLabel style={{ color: '#e64e58' }}>Delete Account</SettingLabel>
                                        <SettingValue style={{ fontSize: '14px' }}>
                                            Permanently remove your account and all data
                                        </SettingValue>
                                    </SettingDetails>
                                </SettingInfo>
                                <MobergButton
                                    theme={MobergTheme.RED}
                                    variant={MobergButtonVariant.CLEAR}
                                    shape={MobergButtonShape.RECTANGLE}
                                    onClick={renderDeleteAccountModal}
                                >
                                    Delete
                                </MobergButton>
                            </SettingRow>
                        </CardContent>
                    </SectionCard>

                    <SectionCard>
                        <CardHeader>
                            <SectionTitle>System Information</SectionTitle>
                        </CardHeader>
                        <CardContent>
                            <SettingRow>
                                <SettingInfo>
                                    <IconWrapper>
                                        <MdIcons.MdCode size={20} style={{ color: 'var(--text-secondary-color)' }} />
                                    </IconWrapper>
                                    <SettingDetails>
                                        <SettingLabel>Software Version</SettingLabel>
                                        <SettingValue>MCP v{SOFTWARE_VERSION}</SettingValue>
                                    </SettingDetails>
                                </SettingInfo>
                            </SettingRow>
                        </CardContent>
                    </SectionCard>
                </ContentContainer>
            </ModalScrollbar>
        </ModalContainer>
    )
}

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--color-surface-panel);
    width: 820px;
    max-height: 776px;
`;

const ContentContainer = styled.div`
    max-width: 700px;
    margin: 0 auto;
    padding: 24px 0;
`;

const SectionCard = styled.div`
    background: var(--color-surface-menu);
    border-radius: 8px;
    box-shadow: var(--modal-box-shadow);
    margin-bottom: 24px;
    overflow: hidden;

    &:last-child {
        margin-bottom: 0;
    }
`;

const CardHeader = styled.div`
    padding: 20px 24px 0 24px;
`;

const SectionTitle = styled.h2`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    color: var(--color-text-primary);
    line-height: 150%;
    margin: 0;
`;

const CardDescription = styled.p`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    color: var(--text-secondary-color);
    line-height: 150%;
    margin: 8px 0 0 0;
    padding: 0 24px 16px 24px;
`;

const CardContent = styled.div`
    padding: 0 24px 16px 24px;
`;

const SettingRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    transition: background-color 0.2s ease;
    
    &:first-child {
        padding-top: 8px;
    }
    
    &:last-child {
        padding-bottom: 8px;
    }
`;

const SettingInfo = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: var(--color-hover-neutral);
    flex-shrink: 0;
    margin-right: 16px;
`;

const SettingDetails = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

const SettingLabel = styled.div`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    color: var(--color-text-primary);
    line-height: 150%;
    margin-bottom: 2px;
`;

const SettingValue = styled.div`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: var(--text-secondary-color);
    line-height: 150%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SettingDivider = styled.div`
    height: 1px;
    background: var(--color-border-default);
    margin: 0 -8px;
`;

export default AccountSettingsModal;